import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import LanguageSelector from './LanguageSelector';
import NotificationBell from './NotificationBell';
import FeedbackWidget from './FeedbackWidget';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  LayoutDashboard,
  Send,
  FileText,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Building2,
  BookOpen,
  Calculator,
  MessageSquare,
  Radio,
  Activity,
  DollarSign,
  GripVertical,
  Pencil,
  Check,
  X,
  Key,
  Code,
  UserPlus,
  Sun,
  Moon,
  Ban,
  Archive,
  Eye,
  EyeOff,
  Terminal,
  Book,
  Database
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Map icon names to components
const ICON_MAP = {
  LayoutDashboard,
  Send,
  FileText,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Building2,
  BookOpen,
  Calculator,
  MessageSquare,
  Radio,
  Activity,
  DollarSign,
  Key,
  Code,
  UserPlus,
  Ban,
  Archive,
  Terminal,
  Book,
  Database,
  Smartphone: MessageSquare
};

// Default menu configuration - Nouvelle structure avec modules fusionnés
const DEFAULT_MENU_CONFIG = [
  // Section principale
  { id: 'dashboard', path: '/dashboard', pathSuperAdmin: '/superadmin', labelKey: 'nav.dashboard', icon: 'LayoutDashboard', showTo: ['superadmin', 'admin', 'user'] },
  { id: 'rcs-campaign', path: '/rcs-campaign', label: 'Campagnes RCS', icon: 'Send', showTo: ['superadmin', 'admin', 'user'] },
  { id: 'contacts', path: '/contacts', labelKey: 'nav.contacts', icon: 'Users', showTo: ['superadmin', 'admin', 'user'] },
  { id: 'data-management', path: '/data-management', label: 'Gestion des données', icon: 'Database', showTo: ['superadmin', 'admin', 'user'] },
  { id: 'events-analytics', path: '/events-analytics', label: 'Statistiques', icon: 'BarChart3', showTo: ['superadmin', 'admin', 'user'] },
  { id: 'templates-hub', path: '/templates-hub', label: 'Templates', icon: 'FileText', showTo: ['superadmin', 'admin', 'user'] },
  { id: 'archives', path: '/archives', label: 'Archives', icon: 'Archive', showTo: ['superadmin', 'admin', 'user'] },
  { id: 'blacklist', path: '/blacklist', label: 'Blacklist', icon: 'Ban', showTo: ['superadmin', 'admin', 'user'] },
  { id: 'cost-simulator', path: '/cost-simulator', labelKey: 'nav.simulator', icon: 'Calculator', showTo: ['superadmin', 'admin', 'user'], permission: 'cost_simulator_enabled' },
  
  // Séparateur
  { id: 'separator-1', type: 'separator', showTo: ['superadmin', 'admin'] },
  
  // Section administration (SUPADMIN)
  { id: 'sms-mailing', path: '/sms/mailing', label: 'SMS Mailing', icon: 'Smartphone', showTo: ['superadmin'] },
  { id: 'clients', path: '/superadmin', label: 'Clients', icon: 'Building2', showTo: ['superadmin'] },
  { id: 'admin-advanced', path: '/admin-advanced', label: 'Administration', icon: 'Activity', showTo: ['superadmin'] },
  { id: 'api-hub', path: '/api-hub', label: 'API', icon: 'Code', showTo: ['superadmin', 'admin'], permission: 'api_access' },
  { id: 'onboarding-hub', path: '/onboarding-hub', label: 'Configuration', icon: 'Settings', showTo: ['superadmin'] },
  { id: 'billing', path: '/billing', labelKey: 'nav.billing', icon: 'DollarSign', showTo: ['superadmin'] },
  
  // Séparateur bas
  { id: 'separator-2', type: 'separator', showTo: ['superadmin'] },
  
  // Admin tools (SUPADMIN uniquement)
  { id: 'logs', path: '/logs', label: 'Logs Système', icon: 'Terminal', showTo: ['superadmin'] },
  { id: 'documentation', path: '/documentation', label: 'Documentation', icon: 'Book', showTo: ['superadmin'] },
];

// Sortable menu item component
const SortableMenuItem = ({ item, isActive, isEditMode, isSuperAdmin, t, onNavigate, onToggleVisibility, isHidden }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.id, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : isHidden ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 'auto'
  };

  const Icon = ICON_MAP[item.icon] || LayoutDashboard;
  const label = item.labelKey ? t(item.labelKey) : item.label;
  const path = isSuperAdmin && item.pathSuperAdmin ? item.pathSuperAdmin : item.path;

  if (isEditMode) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center space-x-2 rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
          isDragging 
            ? 'bg-indigo-100 shadow-lg' 
            : isHidden
              ? 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
              : isActive 
                ? 'bg-indigo-50 text-indigo-700' 
                : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700'
        }`}
      >
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
          data-testid={`drag-handle-${item.id}`}
        >
          <GripVertical className="h-4 w-4 text-slate-400" />
        </button>
        <Icon className={`h-5 w-5 ${isHidden ? 'text-slate-400' : ''}`} />
        <span className="flex-1">{label}</span>
        {/* Toggle visibility button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility && onToggleVisibility(item.id);
          }}
          className={`p-1 rounded transition-colors ${
            isHidden 
              ? 'bg-slate-200 dark:bg-slate-600 text-slate-500' 
              : 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400'
          }`}
          title={isHidden ? 'Afficher' : 'Masquer'}
        >
          {isHidden ? (
            <EyeOff className="h-3.5 w-3.5" />
          ) : (
            <Eye className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    );
  }

  // Don't render if hidden (for non-edit mode)
  if (isHidden) {
    return null;
  }

  return (
    <Link
      ref={setNodeRef}
      style={style}
      to={path}
      onClick={onNavigate}
      data-testid={`nav-${item.id}`}
      className={`flex items-center space-x-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
        isActive
          ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-[hsl(220,12%,65%)] dark:hover:bg-[hsl(220,13%,18%)] dark:hover:text-[hsl(220,13%,85%)]'
      }`}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </Link>
  );
};

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, isSuperAdmin, isImpersonating, impersonation } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [permissions, setPermissions] = useState({
    cost_simulator_enabled: true,
    onboarding_access: false,
    api_access: true
  });
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [menuConfig, setMenuConfig] = useState(DEFAULT_MENU_CONFIG);
  const [originalConfig, setOriginalConfig] = useState(DEFAULT_MENU_CONFIG);
  const [hiddenItems, setHiddenItems] = useState(new Set()); // Items hidden by SUPADMIN
  const [originalHiddenItems, setOriginalHiddenItems] = useState(new Set());

  // Load menu config from localStorage or API
  useEffect(() => {
    const loadMenuConfig = async () => {
      try {
        // Try to load from localStorage first
        const savedConfig = localStorage.getItem('b2rcs_menu_config');
        const savedHidden = localStorage.getItem('b2rcs_hidden_items');
        const menuVersion = localStorage.getItem('b2rcs_menu_version');
        const CURRENT_MENU_VERSION = '2.5'; // Increment when adding new menu items - FORCE RESET
        
        // Required menu items that must exist
        const REQUIRED_ITEMS = [
          { id: 'separator-2', type: 'separator', showTo: ['superadmin'] },
          { id: 'logs', path: '/logs', label: 'Logs Système', icon: 'Terminal', showTo: ['superadmin'] },
          { id: 'documentation', path: '/documentation', label: 'Documentation', icon: 'Book', showTo: ['superadmin'] },
        ];
        
        // Helper function to ensure required items exist
        const ensureRequiredItems = (config) => {
          const existingIds = new Set(config.map(item => item.id));
          const missingItems = REQUIRED_ITEMS.filter(item => !existingIds.has(item.id));
          if (missingItems.length > 0) {
            console.log('Adding missing menu items:', missingItems.map(i => i.id).join(', '));
            return [...config, ...missingItems];
          }
          return config;
        };
        
        // Force reset if menu version is outdated or missing
        if (!menuVersion || menuVersion !== CURRENT_MENU_VERSION) {
          console.log(`Menu version outdated (${menuVersion} vs ${CURRENT_MENU_VERSION}), resetting to defaults`);
          localStorage.removeItem('b2rcs_menu_config');
          localStorage.removeItem('b2rcs_hidden_items');
          localStorage.setItem('b2rcs_menu_version', CURRENT_MENU_VERSION);
          localStorage.setItem('b2rcs_menu_config', JSON.stringify(DEFAULT_MENU_CONFIG));
          setMenuConfig(DEFAULT_MENU_CONFIG);
          setOriginalConfig(DEFAULT_MENU_CONFIG);
          setHiddenItems(new Set());
          setOriginalHiddenItems(new Set());
          return; // Don't continue with the rest of loading
        }
        
        // Helper function to merge new items from default config into saved config
        const mergeMenuConfigs = (saved, defaults) => {
          const savedIds = new Set(saved.map(item => item.id));
          const newItems = defaults.filter(item => !savedIds.has(item.id));
          
          if (newItems.length > 0) {
            console.log('Menu migration: Adding new items:', newItems.map(i => i.id).join(', '));
            // Add new items to the end (but before separators if applicable)
            return [...saved, ...newItems];
          }
          return saved;
        };
        
        if (savedConfig) {
          try {
            const parsed = JSON.parse(savedConfig);
            // Validate parsed config - must be non-empty array with valid items
            if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(item => item.id && (item.type === 'separator' || item.path))) {
              // Merge with defaults and ensure required items
              let mergedConfig = mergeMenuConfigs(parsed, DEFAULT_MENU_CONFIG);
              mergedConfig = ensureRequiredItems(mergedConfig);
              
              // Save merged config if there were changes
              if (mergedConfig.length !== parsed.length) {
                localStorage.setItem('b2rcs_menu_config', JSON.stringify(mergedConfig));
              }
              
              setMenuConfig(mergedConfig);
              setOriginalConfig(mergedConfig);
              // Successfully loaded from localStorage, don't call API
              return;
            } else {
              // Invalid config structure, clear it
              console.warn('Invalid menu config in localStorage, resetting to default');
              localStorage.removeItem('b2rcs_menu_config');
            }
          } catch (parseError) {
            // JSON parse error, clear corrupted data
            console.warn('Corrupted menu config in localStorage, resetting');
            localStorage.removeItem('b2rcs_menu_config');
          }
        }
        
        // Load hidden items
        if (savedHidden) {
          try {
            const parsedHidden = JSON.parse(savedHidden);
            if (Array.isArray(parsedHidden)) {
              setHiddenItems(new Set(parsedHidden));
              setOriginalHiddenItems(new Set(parsedHidden));
            }
          } catch {
            localStorage.removeItem('b2rcs_hidden_items');
          }
        }
        
        // Try to load from API if localStorage failed
        try {
          const response = await axios.get(`${API_URL}/settings/menu-config`);
          if (response.data?.menu && Array.isArray(response.data.menu) && response.data.menu.length > 0) {
            setMenuConfig(response.data.menu);
            setOriginalConfig(response.data.menu);
            localStorage.setItem('b2rcs_menu_config', JSON.stringify(response.data.menu));
            return;
          }
        } catch {
          // API not available, use default
        }
        
        // Fallback to default config
        setMenuConfig(DEFAULT_MENU_CONFIG);
        setOriginalConfig(DEFAULT_MENU_CONFIG);
      } catch (error) {
        console.error('Error loading menu config:', error);
        setMenuConfig(DEFAULT_MENU_CONFIG);
        setOriginalConfig(DEFAULT_MENU_CONFIG);
      }
    };
    loadMenuConfig();
  }, []);

  // Load permissions for non-superadmin users OR when impersonating
  useEffect(() => {
    const fetchPermissions = async () => {
      // Get client_id - from user (which is impersonated user during impersonation)
      // During impersonation, user.client_id contains the impersonated client's ID
      const clientId = user?.client_id;
      
      // Debug logging
      console.log('Permission check:', { 
        clientId, 
        isSuperAdmin, 
        isImpersonating,
        userRole: user?.role,
        impersonationClient: impersonation?.client?.id 
      });
      
      // Fetch permissions if we have a client_id and either:
      // 1. User is not superadmin, OR
      // 2. User is impersonating a client
      if (clientId && (!isSuperAdmin || isImpersonating)) {
        try {
          const response = await axios.get(`${API_URL}/clients/${clientId}/permissions`);
          console.log('Fetched permissions for client', clientId, ':', response.data);
          setPermissions(response.data);
        } catch (error) {
          console.error('Error fetching permissions:', error);
          // Reset to defaults on error
          setPermissions({
            cost_simulator_enabled: true,
            onboarding_access: false,
            api_access: true
          });
        }
      } else if (isSuperAdmin && !isImpersonating) {
        // SuperAdmin not impersonating has all permissions
        setPermissions({
          cost_simulator_enabled: true,
          onboarding_access: true,
          api_access: true
        });
      }
      setLoading(false);
    };
    fetchPermissions();
  }, [user, isSuperAdmin, isImpersonating, impersonation]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setMenuConfig((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  // Toggle item visibility
  const handleToggleVisibility = useCallback((itemId) => {
    setHiddenItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Save menu configuration
  const handleSaveConfig = async () => {
    try {
      // Save to localStorage
      localStorage.setItem('b2rcs_menu_config', JSON.stringify(menuConfig));
      localStorage.setItem('b2rcs_hidden_items', JSON.stringify([...hiddenItems]));
      
      // Try to save to API
      try {
        await axios.post(`${API_URL}/settings/menu-config`, { 
          menu: menuConfig,
          hidden_items: [...hiddenItems]
        });
      } catch {
        // API not available, just use localStorage
      }
      
      setOriginalConfig(menuConfig);
      setOriginalHiddenItems(new Set(hiddenItems));
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving menu config:', error);
    }
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setMenuConfig(originalConfig);
    setHiddenItems(new Set(originalHiddenItems));
    setIsEditMode(false);
  };

  // Filter visible menu items based on role and permissions
  const visibleItems = useMemo(() => {
    // When impersonating, treat as non-superadmin and use impersonated client's role
    const effectiveIsSuperAdmin = isSuperAdmin && !isImpersonating;
    
    // Determine effective role:
    // - If impersonating: use the impersonated client's role (admin or user)
    // - If superadmin not impersonating: superadmin
    // - Otherwise: user's actual role
    let effectiveRole = user?.role || 'user';
    if (isImpersonating) {
      // During impersonation, treat as 'admin' (client user) not 'superadmin'
      effectiveRole = impersonation?.client?.role || 'admin';
    }
    
    const filterItems = (items) => {
      return items.filter(item => {
        // Separators are always shown based on surrounding items
        if (item.type === 'separator') {
          // For simplicity, show separators only to superadmin not impersonating
          return effectiveIsSuperAdmin && item.showTo?.includes('superadmin');
        }
        
        // Check role visibility
        if (!item.showTo?.includes(effectiveRole)) {
          // During impersonation, also check if 'admin' is allowed (client user perspective)
          if (isImpersonating && item.showTo?.includes('admin')) {
            // Continue to permission check
          } else {
            return false;
          }
        }
        
        // Check permission if required - always apply when impersonating
        if (item.permission) {
          // SuperAdmin not impersonating has all permissions
          if (effectiveIsSuperAdmin) {
            return true;
          }
          // Check actual permission from client settings
          return permissions[item.permission] === true;
        }
        
        return true;
      });
    };
    
    if (!menuConfig || menuConfig.length === 0) {
      return filterItems(DEFAULT_MENU_CONFIG);
    }
    
    return filterItems(menuConfig);
  }, [menuConfig, user?.role, isSuperAdmin, isImpersonating, impersonation, permissions]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if current path is active
  const isPathActive = (item) => {
    const path = isSuperAdmin && item.pathSuperAdmin ? item.pathSuperAdmin : item.path;
    return location.pathname === path;
  };

  if (loading && !isSuperAdmin && user?.client_id) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[hsl(220,18%,10%)] transition-colors duration-200" data-testid="layout-container">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 border-r border-slate-100 dark:border-[hsl(220,14%,18%)] bg-white/50 dark:bg-[hsl(220,17%,13%)]/95 backdrop-blur-md z-50 flex flex-col" data-testid="sidebar">
        {/* Logo */}
        <div className="flex h-16 items-center border-b border-slate-100 dark:border-[hsl(220,14%,18%)] px-6">
          <Building2 className="h-8 w-8 text-indigo-700 dark:text-indigo-400" />
          <span className="ml-3 font-bold text-xl dark:text-[hsl(220,13%,85%)]" style={{ fontFamily: 'Manrope, sans-serif' }}>B2RCS</span>
        </div>
        
        {/* Edit Mode Toggle (SuperAdmin only, not during impersonation) */}
        {isSuperAdmin && !isImpersonating && (
          <div className="px-4 py-3 border-b border-slate-100 dark:border-[hsl(220,13%,18%)] bg-slate-50/50 dark:bg-[hsl(220,13%,13%)]/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Pencil className="h-4 w-4 text-slate-500 dark:text-[hsl(220,12%,50%)]" />
                <span className="text-xs font-medium text-slate-600 dark:text-[hsl(220,12%,60%)]">Mode édition</span>
              </div>
              <Switch
                checked={isEditMode}
                onCheckedChange={setIsEditMode}
                data-testid="edit-mode-toggle"
              />
            </div>
            {isEditMode && (
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  variant="default"
                  onClick={handleSaveConfig}
                  className="flex-1 h-8 text-xs"
                  data-testid="save-menu-config"
                >
                  <Check className="h-3 w-3 mr-1" />
                  Sauver
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelEdit}
                  className="flex-1 h-8 text-xs"
                  data-testid="cancel-menu-edit"
                >
                  <X className="h-3 w-3 mr-1" />
                  Annuler
                </Button>
              </div>
            )}
            {isEditMode && (
              <p className="text-[10px] text-slate-400 dark:text-[hsl(220,12%,45%)] mt-2">
                Glissez-déposez pour réorganiser le menu
              </p>
            )}
          </div>
        )}
        
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 pb-8" data-testid="nav-menu" style={{ maxHeight: 'calc(100vh - 250px)' }}>
          {isEditMode ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={(menuConfig && menuConfig.length > 0 ? menuConfig : DEFAULT_MENU_CONFIG).map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1">
                  {(menuConfig && menuConfig.length > 0 ? menuConfig : DEFAULT_MENU_CONFIG).map((item) => (
                    item.type === 'separator' ? (
                      <div key={item.id} className="my-3 mx-2 border-t border-slate-200 dark:border-[hsl(220,13%,20%)]" />
                    ) : (
                      <SortableMenuItem
                        key={item.id}
                        item={item}
                        isActive={isPathActive(item)}
                        isEditMode={isEditMode}
                        isSuperAdmin={isSuperAdmin}
                        t={t}
                        onToggleVisibility={handleToggleVisibility}
                        isHidden={hiddenItems.has(item.id)}
                      />
                    )
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="space-y-1">
              {visibleItems.map((item) => (
                item.type === 'separator' ? (
                  <div key={item.id} className="my-3 mx-2 border-t border-slate-200 dark:border-[hsl(220,13%,20%)]" />
                ) : (
                  <SortableMenuItem
                    key={item.id}
                    item={item}
                    isActive={isPathActive(item)}
                    isEditMode={false}
                    isSuperAdmin={isSuperAdmin}
                    t={t}
                    isHidden={hiddenItems.has(item.id)}
                  />
                )
              ))}
            </div>
          )}
        </nav>
        
        {/* User Info & Logout */}
        <div className="border-t border-slate-100 dark:border-[hsl(220,13%,18%)] p-4 mt-auto">
          <div className="mb-3 rounded-lg bg-slate-50 dark:bg-[hsl(220,13%,16%)] p-3" data-testid="user-info">
            <p className="text-sm font-semibold text-slate-900 dark:text-[hsl(220,13%,82%)]">{user?.name}</p>
            <p className="text-xs text-slate-500 dark:text-[hsl(220,12%,50%)]">{user?.email}</p>
            <p className="mt-1 text-xs font-medium text-indigo-600 dark:text-indigo-400">
              {user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'Utilisateur'}
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-[hsl(0,40%,15%)] dark:hover:text-red-300"
            data-testid="logout-button"
          >
            <LogOut className="mr-3 h-4 w-4" />
            {t('common.logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 h-16 border-b border-slate-100 dark:border-[hsl(220,13%,18%)] bg-white/70 dark:bg-[hsl(220,15%,12%)]/90 backdrop-blur-xl" data-testid="top-bar">
          <div className="flex h-full items-center justify-end px-8">
            <div className="flex items-center gap-4">
              <FeedbackWidget />
              
              <NotificationBell />
              
              {/* Dark Mode Toggle */}
              <div 
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-[hsl(220,13%,16%)] transition-colors"
                data-testid="dark-mode-toggle-container"
              >
                <Sun className={`h-4 w-4 transition-colors ${isDarkMode ? 'text-[hsl(220,12%,40%)]' : 'text-amber-500'}`} />
                <Switch
                  checked={isDarkMode}
                  onCheckedChange={toggleDarkMode}
                  data-testid="dark-mode-toggle"
                  className="data-[state=checked]:bg-indigo-600"
                />
                <Moon className={`h-4 w-4 transition-colors ${isDarkMode ? 'text-indigo-400' : 'text-slate-400'}`} />
              </div>
              
              <LanguageSelector />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
